<template>
  <DefaultField
    :field="field"
    :errors="errors"
    :show-help-text="showHelpText"
    :full-width-content="fullWidthContent"
  >
    <template #field>
      <!-- Searchable Mode -->
      <div v-if="isSearchable" class="space-y-2">
        <!-- Selected Resources Display -->
        <div v-if="selectedResources.length > 0" class="flex flex-wrap gap-2 mb-2">
          <span
            v-for="resource in selectedResources"
            :key="resource.value"
            class="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200"
          >
            <span v-if="resource.avatar" class="mr-2">
              <img
                :src="resource.avatar"
                class="w-5 h-5 rounded-full"
              />
            </span>
            {{ resource.display }}
            <button
              type="button"
              @click="removeResource(resource.value)"
              class="ml-2 text-primary-600 dark:text-primary-300 hover:text-primary-800 dark:hover:text-primary-100"
            >
              ×
            </button>
          </span>
        </div>

        <!-- Search Input -->
        <SearchInput
          :dusk="`${field.attribute}-search-input`"
          :disabled="field.readonly"
          @input="performResourceSearch"
          @selected="addResource"
          :error="hasError"
          :debounce="field.debounce || 500"
          :value="null"
          :data="filteredResources"
          :clearable="true"
          trackBy="value"
          class="w-full"
        >
          <div class="text-gray-400 dark:text-gray-400">
            {{ __('Type to search...') }}
          </div>

          <template #option="{ selected, option }">
            <SearchInputResult
              :option="option"
              :selected="selected"
              :with-subtitles="field.withSubtitles"
            />
          </template>
        </SearchInput>
      </div>

      <!-- Non-Searchable Mode -->
      <div v-else>
        <select
          v-model="selected"
          multiple
          class="form-select block w-full"
          @change="handleChange"
          :disabled="field.readonly"
        >
          <option v-for="(label, value) in field.options" :key="value" :value="value">
            {{ label }}
          </option>
        </select>
      </div>
    </template>
  </DefaultField>
</template>

<script>
import { DependentFormField, HandlesValidationErrors, PerformsSearches } from '@/mixins'
import DefaultField from '@/components/DefaultField'

import storage from '@/storage/BelongsToFieldStorage'
import find from 'lodash/find'
import filled from '@/util/filled'

export default {
  mixins: [DependentFormField, HandlesValidationErrors, PerformsSearches],

  components: {
    DefaultField,
  },

  props: ['resourceName', 'field', 'resourceId'],

  data() {
    return {
      selected: [],
      selectedResources: [],
      availableResources: [],
      search: '',
    };
  },

  computed: {
    /**
     * Determine if the field is searchable
     */
    isSearchable() {
      return Boolean(this.field.searchable);
    },

    /**
     * Get filtered resources for search input
     */
    filteredResources() {
      if (!this.isSearchable) {
        return [];
      }

      // Filter out already selected resources
      const selectedValues = this.selectedResources.map(r => r.value);
      return this.availableResources.filter(
        resource => !selectedValues.includes(resource.value)
      );
    },

    /**
     * Get query params for fetching resources
     */
    queryParams() {
      const selectedValues = Array.isArray(this.selected) 
        ? this.selected 
        : [];
      
      return {
        current: selectedValues.join(','),
        first: 'false',
        search: this.search || '',
      };
    },
  },

  mounted() {
    this.initializeComponent();
  },

  methods: {
    /**
     * Initialize the component
     */
    initializeComponent() {
      // Initialize selected values from field value
      if (this.field.value) {
        if (Array.isArray(this.field.value)) {
          this.selected = this.field.value.map((item) => 
            typeof item === 'object' ? item.id : item
          );
        } else {
          this.selected = [];
        }
      }

      if (this.isSearchable) {
        // Load initial selected resources if editing
        if (this.selected.length > 0) {
          this.loadSelectedResources();
        }
      }

      this.field.fill = this.fill;
    },

    /**
     * Load selected resources for display
     */
    loadSelectedResources() {
      if (!this.field.resourceName) {
        return;
      }

      const params = {
        current: this.selected.join(','),
        first: 'true',
      };

      storage
        .fetchAvailableResources(this.resourceName, this.field.attribute, {
          params,
        })
        .then(({ data: { resources } }) => {
          this.selectedResources = resources.filter(r =>
            this.selected.includes(r.value)
          );
        });
    },

    /**
     * Get available resources for search
     */
    getAvailableResources(search = '') {
      if (!this.isSearchable || !this.field.resourceName) {
        return Promise.resolve();
      }

      // Update search value
      if (search !== undefined) {
        this.search = search;
      }

      // Don't search if search is empty and field doesn't allow empty search
      if (!this.search || !this.search.trim()) {
        this.availableResources = [];
        return Promise.resolve();
      }

      Nova.$progress.start();

      return storage
        .fetchAvailableResources(this.resourceName, this.field.attribute, {
          params: {
            current: this.selected.join(','),
            first: 'false',
            search: this.search,
          },
        })
        .then(({ data: { resources } }) => {
          Nova.$progress.done();
          this.availableResources = resources;
        })
        .catch(() => {
          Nova.$progress.done();
          this.availableResources = [];
        });
    },

    /**
     * Perform resource search
     */
    performResourceSearch(search) {
      if (this.isSearchable) {
        this.performSearch(search);
      }
    },

    /**
     * Add a resource to selected list
     */
    addResource(resource) {
      if (!resource || this.selected.includes(resource.value)) {
        return;
      }

      this.selected.push(resource.value);
      this.selectedResources.push(resource);
      this.handleChange();
    },

    /**
     * Remove a resource from selected list
     */
    removeResource(value) {
      this.selected = this.selected.filter(v => v !== value);
      this.selectedResources = this.selectedResources.filter(r => r.value !== value);
      this.handleChange();
    },

    /**
     * Handle change event
     */
    handleChange() {
      this.emitFieldValueChange(this.field.attribute, this.selected);
    },

    /**
     * Fill the form data
     */
    fill(formData) {
      this.fillIfVisible(
        formData,
        this.field.attribute,
        JSON.stringify(this.selected)
      );
    },
  },
};
</script>